import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Calendar, XCircle, Pencil, Plus, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { dealService, type Deal } from '@/services/deal.service';

export function ScheduledDealsPage() {
  const queryClient = useQueryClient();

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ['scheduled-deals'],
    queryFn: dealService.getScheduledDeals,
  });

  const cancelMutation = useMutation({
    mutationFn: dealService.cancelDeal,
    onSuccess: () => {
      toast.success('Scheduled deal cancelled');
      queryClient.invalidateQueries({ queryKey: ['scheduled-deals'] });
    },
    onError: () => toast.error('Failed to cancel deal'),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Sort by scheduled time
  const sorted = [...deals].sort((a, b) => {
    const aTime = a.scheduledFor ? new Date(a.scheduledFor).getTime() : 0;
    const bTime = b.scheduledFor ? new Date(b.scheduledFor).getTime() : 0;
    return aTime - bTime;
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Calendar className="h-8 w-8" />
            Scheduled Deals
          </h1>
          <p className="mt-1 text-muted-foreground">
            Deals queued to go live at a future time
          </p>
        </div>
        <Link to="/deals/create">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Schedule New Deal
          </Button>
        </Link>
      </div>

      {sorted.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="mb-4 h-12 w-12 text-gray-300" />
            <p className="text-lg text-muted-foreground">No scheduled deals</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Create a deal and set a future publish time
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sorted.map((deal) => (
            <ScheduledDealCard
              key={deal.id}
              deal={deal}
              onCancel={() => {
                if (confirm('Cancel this scheduled deal?')) {
                  cancelMutation.mutate(deal.id);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduledDealCard({
  deal,
  onCancel,
}: {
  deal: Deal;
  onCancel: () => void;
}) {
  const scheduledDate = deal.scheduledFor ? new Date(deal.scheduledFor) : null;
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (!scheduledDate) return;

    const update = () => {
      const diff = Math.max(0, scheduledDate.getTime() - Date.now());
      if (diff === 0) {
        setCountdown('Publishing now...');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(
        h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`
      );
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [scheduledDate?.getTime()]);

  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        {deal.item.photoUrl ? (
          <img
            src={deal.item.photoUrl}
            alt={deal.item.name}
            className="h-16 w-16 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100">
            <Clock className="h-6 w-6 text-gray-400" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">{deal.item.name}</h3>
          <p className="text-sm text-muted-foreground">
            {deal.item.originalPrice} {deal.item.currency} | {deal.slotsTotal} slots |{' '}
            {deal.item.category}
          </p>
        </div>

        <div className="text-right">
          {countdown && (
            <div className="text-lg font-semibold tabular-nums text-blue-600">
              {countdown}
            </div>
          )}
          <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {scheduledDate ? (
              <span>{format(scheduledDate, 'MMM d, h:mm a')}</span>
            ) : (
              <span>--</span>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Link to={`/deals/create?duplicate=${deal.id}`}>
            <Button size="sm" variant="outline">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <Button size="sm" variant="destructive" onClick={onCancel}>
            <XCircle className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
